import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip, Tooltip, Typography, Button, Badge, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import {
  HEALTH_STATUS_COLORS,
  VISIT_COLORS,
  SP_VISIT_COLORS,
  getSpVisitAge,
} from "../../constants/clinicalDashboard";
import TableSearchBar from "./TableSearchBar";
import { useObservationsModal } from "../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../components/Advanced/DataGrid/observationsColumn";

const STATUS_OPTIONS = ["All", "Well", "Acute", "Chronic", "At Risk", "Complex", "End of Life"];
const CAREPLAN_OPTIONS = ["All", "With Care Plan", "Without Care Plan"];
const SP_PATIENT_OPTIONS = ["All", "SP Patients", "Non-SP Patients"];

/**
 * Tabla de pacientes high-risk sin Care Manager asignado.
 * Incluye indicadores de Care Plan y SantePlus patient.
 *
 * @param {{ patients: Array, onCreateTask: (row) => void }} props
 */
export default function UnmanagedRiskTable({ patients, onCreateTask }) {
  const navigate = useNavigate();
  const { showObservationsModal } = useObservationsModal();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [carePlanFilter, setCarePlanFilter] = useState("All");
  const [spPatientFilter, setSpPatientFilter] = useState("All");

  const filteredRows = useMemo(() => {
    if (!patients) return [];
    const lowerSearch = search.toLowerCase();
    return patients.filter((row) => {
      if (lowerSearch && !row.name?.toLowerCase().includes(lowerSearch)) return false;
      if (statusFilter !== "All" && row.healthStatus !== statusFilter) return false;
      if (carePlanFilter === "With Care Plan" && !row.hasCarePlan) return false;
      if (carePlanFilter === "Without Care Plan" && row.hasCarePlan) return false;
      if (spPatientFilter === "SP Patients" && !row.isSantePlusPatient) return false;
      if (spPatientFilter === "Non-SP Patients" && row.isSantePlusPatient) return false;
      return true;
    });
  }, [patients, search, statusFilter, carePlanFilter, spPatientFilter]);

  const columns = [
    {
      field: "name",
      headerName: "Patient",
      flex: 1.4,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} noWrap>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "healthStatus",
      headerName: "Status",
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) => {
        const colors = HEALTH_STATUS_COLORS[params.value] || HEALTH_STATUS_COLORS["No Status"];
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor: colors.bg,
              color: colors.text,
              fontWeight: 600,
              fontSize: "0.72rem",
              height: 24,
            }}
          />
        );
      },
    },
    {
      field: "hasCarePlan",
      headerName: "Care Plan",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) =>
        value ? (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            label="Yes"
            size="small"
            sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#DCFCE7", color: "#166534" }}
          />
        ) : (
          <Chip
            icon={<CancelIcon sx={{ fontSize: 14 }} />}
            label="No"
            size="small"
            sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: "#FEE2E2", color: "#991B1B" }}
          />
        ),
    },
    {
      field: "isSantePlusPatient",
      headerName: "SP Patient",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) =>
        value ? (
          <Chip label="Yes" size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#DBEAFE", color: "#1D4ED8" }} />
        ) : (
          <Chip label="No" size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: "#F1F5F9", color: "#94A3B8" }} />
        ),
    },
    {
      field: "globalIndicator",
      headerName: "Global",
      flex: 0.5,
      minWidth: 65,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "conditions",
      headerName: "Conditions",
      flex: 1.4,
      minWidth: 180,
      renderCell: (params) => (
        <Tooltip title={params.value || ""} arrow placement="top">
          <Typography
            variant="body2"
            noWrap
            sx={{ maxWidth: "100%", cursor: "default" }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "openCareGaps",
      headerName: "Gaps",
      flex: 0.4,
      minWidth: 55,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Badge
          badgeContent={params.value}
          color={params.value >= 3 ? "error" : "default"}
          max={99}
          showZero
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.7rem",
              fontWeight: 700,
              minWidth: 20,
              height: 20,
            },
          }}
        />
      ),
    },
    {
      field: "erEvents",
      headerName: "ER",
      flex: 0.4,
      minWidth: 50,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Badge
          badgeContent={params.value}
          color={params.value > 0 ? "error" : "default"}
          max={99}
          showZero
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.7rem",
              fontWeight: 700,
              minWidth: 20,
              height: 20,
            },
          }}
        />
      ),
    },
    {
      field: "medications",
      headerName: "Meds",
      flex: 0.4,
      minWidth: 50,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "lastVisit",
      headerName: "Last Visit",
      flex: 0.7,
      minWidth: 95,
      renderCell: (params) => {
        const colors = VISIT_COLORS[params.value];
        if (!colors) {
          return (
            <Typography variant="body2" color="text.secondary">
              {params.value ?? "N/A"}
            </Typography>
          );
        }
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor: colors.bg,
              color: colors.text,
              fontWeight: 600,
              fontSize: "0.72rem",
              height: 24,
            }}
          />
        );
      },
    },
    {
      field: "lastSantePlusVisit",
      headerName: "SP Visit",
      flex: 0.6,
      minWidth: 85,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const age = getSpVisitAge(value);
        const colors = SP_VISIT_COLORS[age];
        if (!value) {
          return <Chip label="—" size="small" sx={{ height: 22, fontSize: "0.66rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
        }
        const d = new Date(value);
        const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return <Chip label={label} size="small" sx={{ height: 22, fontSize: "0.66rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
      },
    },
    {
      field: "observationsCount",
      headerName: "Obs",
      flex: 0.35,
      minWidth: 50,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const val = value || 0;
        if (val === 0) return <Typography variant="body2" color="text.secondary">0</Typography>;
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#DBEAFE", color: "#2563EB" }} />;
      },
    },
    {
      field: "activeTasks",
      headerName: "Tasks",
      flex: 0.35,
      minWidth: 50,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const val = value || 0;
        if (val === 0) return <Typography variant="body2" color="text.secondary">0</Typography>;
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#FEF3C7", color: "#D97706" }} />;
      },
    },
    {
      field: "careGapsWorked",
      headerName: "Gaps Worked",
      flex: 0.55,
      minWidth: 80,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const w = params.value || 0;
        const t = params.row.openCareGaps || 0;
        if (t === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
        const ratio = w / t;
        let bg, color;
        if (ratio >= 0.75) { bg = "#DCFCE7"; color = "#166534"; }
        else if (ratio >= 0.25) { bg = "#FEF3C7"; color = "#92400E"; }
        else { bg = "#FEE2E2"; color = "#991B1B"; }
        return <Chip label={`${w}/${t}`} size="small" sx={{ height: 20, fontSize: "0.66rem", fontWeight: 700, bgcolor: bg, color }} />;
      },
    },
    getObservationsColumn(showObservationsModal, 3),
    {
      field: "viewPatient",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="View Patient">
          <IconButton size="small" onClick={() => navigate(`/patient-info/${params.row.cipId}`)} sx={{ color: "#6366F1" }}>
            <VisibilityIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<PlaylistAddIcon />}
          onClick={() => onCreateTask(params.row)}
          sx={{
            textTransform: "none",
            fontSize: "0.72rem",
            borderRadius: 1.5,
            lineHeight: 1.2,
          }}
        >
          Add to Worklist
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <TableSearchBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search patient..."
        filters={[
          { key: "healthStatus", label: "Status", options: STATUS_OPTIONS, value: statusFilter },
          { key: "carePlan", label: "Care Plan", options: CAREPLAN_OPTIONS, value: carePlanFilter },
          { key: "spPatient", label: "SP Patient", options: SP_PATIENT_OPTIONS, value: spPatientFilter },
        ]}
        onFilterChange={(key, value) => {
          if (key === "healthStatus") setStatusFilter(value);
          if (key === "carePlan") setCarePlanFilter(value);
          if (key === "spPatient") setSpPatientFilter(value);
        }}
      />

      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.cipId}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          borderRadius: 2,
          border: "none",
          "& .MuiDataGrid-cell": {
            fontSize: "0.78rem",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            fontSize: "0.78rem",
          },
        }}
      />
    </Box>
  );
}
